import { Suspense, useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls } from '@react-three/drei';
import { Veranda } from '../components';
import { useFormStore } from '../state/FormStore';
import { useLocation } from 'react-router-dom';

// Constants for environment presets and lighting intensity
const ENVIRONMENT_PRESET = "night"; // sunset, dawn, night, warehouse, forest, apartment, studio, city, park, lobby
const ENVIRONMENT_HDRI_URL = '/sky/venice_sunset_1k.hdr';

// Camera settings
const cameraSettings = {
    position: [-1.5, 3, 2], // Adjusted position
    fov: 25,
    near: 0.1,
    far: 10 // Adjusted far value
};

const OrderDetails = () => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true); // Loading state
    const { formData, setFormData } = useFormStore();
    const location = useLocation();

    const getOrderIDFromURL = () => {
        const params = new URLSearchParams(location.search);
        return params.get('order_id');
    };

    useEffect(() => {
        const fetchData = async () => {
            const orderId = getOrderIDFromURL();
            if (!orderId) {
                console.error('No order_id found in URL');
                return;
            }

            try {
                const response = await fetch(`https://api.verandadealer.nl/api/configurator/state/${orderId}?action=load_order`);
                const data = await response.json();

                const existingFormData = useFormStore.getState().formData;

                // Merge the new form data with the existing form data and set the total price
                const updatedFormData = {
                    ...existingFormData,
                    ...data.data.data,
                    total: {
                        ...existingFormData.total,
                        price: data.data.price
                    }
                };

                setFormData(updatedFormData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false); // Set loading to false once data is fetched
            }
        };

        fetchData();
    }, [setFormData, location.search]);

    // Extract the relevant data from formData
    const tableData = [
        { name: 'Breedte', value: formData?.maat?.breedte ?? 'N/A' },
        { name: 'Uitval', value: formData?.maat?.uitval ?? 'N/A' },
        { name: 'Doorloop Hoogte', value: formData?.maat?.doorloop_hoogte ?? 'N/A' },
        { name: 'Uitval Type', value: formData?.uitval_type?.uitval_type_option ?? 'N/A' },
        { name: 'Kleur', value: formData?.kleur?.kleur_option ?? 'N/A' },
        { name: 'Dak', value: formData?.dak?.dak_option ?? 'N/A' },
        { name: 'Zonwering', value: formData?.zonwering?.zonwering_option ?? 'N/A' },
        { name: 'Linker Wand', value: formData?.linker?.linker_wand_option ?? 'N/A' },
        { name: 'Linker Spie', value: formData?.linker?.linker_spie_option ?? 'N/A' },
        { name: 'Rechter Wand', value: formData?.rechter?.rechter_wand_option ?? 'N/A' },
        { name: 'Rechter Spie', value: formData?.rechter?.rechter_spie_option ?? 'N/A' },
        { name: 'Goot', value: formData?.gutter?.goot_option ?? 'N/A' },
        { name: 'Schuifdeur', value: formData?.voorkant?.schuifdeur ?? 'N/A' },
        { name: 'Schuifdeur Handle', value: formData?.voorkant_alum_schuifdeur_options?.voorkant_schuifdeur_handle ?? 'N/A' },
        { name: 'Total Price', value: formData?.total?.price ?? 'N/A' },
    ];

    const handleProductClick = (product) => {
        setSelectedProduct(product);
    };

    return (
        <div className="flex flex-col md:flex-row h-screen">
            <div className="md:w-1/2 h-1/2 md:h-full">
                <div className="darzo-craft-container h-full">
                    <Canvas shadows dpr={[1, 1]} style={{ background: '#606074' }} camera={cameraSettings}>
                        <Suspense fallback={null}>
                            <directionalLight castShadow intensity={0.1} position={[2, 0, 0]} />
                            <Veranda />
                            <Environment files={ENVIRONMENT_HDRI_URL} blur={1} background={true} />
                        </Suspense>
                        <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} enableDamping={true} dampingFactor={0.04} minDistance={1} maxDistance={5} maxPolarAngle={Math.PI / 1.8} />
                    </Canvas>
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-10">
                            <div className="text-white text-lg">Loading...</div>
                        </div>
                    )}
                </div>
            </div>
            <div className="md:w-1/2 h-1/2 md:h-full p-4 overflow-auto">
                <h2 className="text-xl font-bold mb-4">Order Details</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full table-auto">
                        <thead>
                            <tr>
                                <th className="px-4 py-2">Name</th>
                                <th className="px-4 py-2">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((item, index) => (
                                <tr key={index} className={`cursor-pointer ${selectedProduct === item ? 'bg-gray-200' : ''}`} onClick={() => handleProductClick(item)}>
                                    <td className="border px-4 py-2">{item.name}</td>
                                    <td className="border px-4 py-2">{item.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default OrderDetails;
